/**
 * Atlas — scope labels + the offerable-scope catalog for the hub.
 *
 * Turns the abstract `Scope` addressing into plain-English labels and builds
 * the list of scopes the picker may show — each gated on `scopeIsOfferable`
 * (≥ MIN_POOL) so we never offer an empty or thin scope. Country/US-state
 * names come from small lookup tables keyed by the codes the pack actually
 * uses, so we only ever name what we can play.
 */
import { REGIONAL_LOCATIONS, availableDifficulties, scopeIsOfferable } from './pack';
import {
  CONTINENTS,
  DIFFICULTIES,
  DIFFICULTY_LABEL,
  type Continent,
  type Difficulty,
  type Scope,
} from './schema';

// ---------------------------------------------------------------------------
// Plain-English labels
// ---------------------------------------------------------------------------

/** Full country names keyed by ISO alpha-2 — only the ones our pack uses. */
const COUNTRY_NAMES: Record<string, string> = {
  FR: 'France',
  IT: 'Italy',
  GB: 'United Kingdom',
  ES: 'Spain',
  GR: 'Greece',
  DE: 'Germany',
  NL: 'Netherlands',
  JP: 'Japan',
  CN: 'China',
  IN: 'India',
  CA: 'Canada',
  MX: 'Mexico',
  BR: 'Brazil',
  AU: 'Australia',
  US: 'United States',
  EG: 'Egypt',
  ZA: 'South Africa',
  TR: 'Turkey',
  RU: 'Russia',
  PE: 'Peru',
  AR: 'Argentina',
  TH: 'Thailand',
  PT: 'Portugal',
  IE: 'Ireland',
  CH: 'Switzerland',
  AT: 'Austria',
  NO: 'Norway',
  SE: 'Sweden',
  NZ: 'New Zealand',
};

/** US state names keyed by two-letter code — only the ones our pack uses. */
const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'Washington, D.C.',
};

/**
 * Full country names DERIVED FROM THE PACK — every regional location carries a
 * full GeoNames `country` name (never a bare ISO code), so the picker can label
 * any offered country without a hand-maintained table. Built once. This is the
 * source of truth that keeps the picker from ever showing a code (BE, CL, …).
 */
const PACK_COUNTRY_NAMES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const l of REGIONAL_LOCATIONS) {
    const cc = l.countryCode.toUpperCase();
    // A full name has letters/spaces; never accept a value that's just the code.
    if (l.country && l.country.toUpperCase() !== cc) map[cc] = l.country;
  }
  return map;
})();

/**
 * Full country name for an ISO alpha-2 code. Prefers the pack's GeoNames name
 * (covers every offered country), then the small static table (for curated
 * codes not in the regional pack), and only ever falls back to the bare code
 * for a country we genuinely can't name.
 */
export function countryName(code: string): string {
  const cc = code.toUpperCase();
  return PACK_COUNTRY_NAMES[cc] ?? COUNTRY_NAMES[cc] ?? cc;
}

export function stateName(code: string): string {
  return STATE_NAMES[code.toUpperCase()] ?? code.toUpperCase();
}

/** Player-facing label for a difficulty tier (Classic / Explorer / Adventurer). */
export function difficultyLabel(difficulty: Difficulty): string {
  return DIFFICULTY_LABEL[difficulty];
}

/** A one-line, plain-English subtitle for the "How challenging?" step. */
export function difficultySubtitle(difficulty: Difficulty): string {
  switch (difficulty) {
    case 'classic':
      return 'Town centers and signs';
    case 'explorer':
      return 'Villages and country roads';
    case 'adventurer':
      return 'Rural roads, open country';
  }
}

/** All 50 US states (+ DC), alphabetical by name — the full state chooser list. */
export const ALL_US_STATE_CODES: readonly string[] = Object.keys(STATE_NAMES).sort((a, b) =>
  stateName(a).localeCompare(stateName(b)),
);

/** Plain label for any scope. */
export function scopeLabel(scope: Scope): string {
  switch (scope.kind) {
    case 'world':
      return 'Around the World';
    case 'landmarks':
      return 'Famous Landmarks';
    case 'continent':
      return scope.continent;
    case 'country':
      return countryName(scope.countryCode);
    case 'usState':
      return stateName(scope.stateCode);
  }
}

// ---------------------------------------------------------------------------
// The offerable-scope catalog
// ---------------------------------------------------------------------------

export type ScopeFamily = 'world' | 'landmarks' | 'continent' | 'country' | 'usState';

/**
 * Distinct country codes present in the REGIONAL pool (the regional scopes draw
 * from `REGIONAL_PACK`, not the curated landmarks). US is included — the country
 * scope is offerable for the US too, though the picker leans on the state list.
 */
function countryCodesInRegional(): string[] {
  const set = new Set<string>();
  for (const l of REGIONAL_LOCATIONS) set.add(l.countryCode.toUpperCase());
  return [...set];
}

/** Continents that clear the offerable bar at SOME difficulty. */
export function offerableContinents(): Continent[] {
  return CONTINENTS.filter((c) => scopeIsOfferable({ kind: 'continent', continent: c }));
}

/** Countries that clear the offerable bar at some difficulty — sorted by name. */
export function offerableCountries(): string[] {
  return countryCodesInRegional()
    .filter((code) => scopeIsOfferable({ kind: 'country', countryCode: code }))
    .sort((a, b) => countryName(a).localeCompare(countryName(b)));
}

/**
 * EVERY US state (+ DC), alphabetical — the full 50-state chooser. Each carries
 * its own `offerable` flag (≥ MIN_POOL at some difficulty) so the picker can
 * list all 50 while disabling the ones the pack can't cover yet. We list all 50
 * deliberately (CONTRACT v2: "all 50 states selectable") rather than hiding the
 * thin ones — a senior expects to find their state.
 */
export interface StateEntry {
  code: string;
  name: string;
  offerable: boolean;
}

export function allStatesForPicker(): StateEntry[] {
  return ALL_US_STATE_CODES.map((code) => ({
    code,
    name: stateName(code),
    offerable: scopeIsOfferable({ kind: 'usState', stateCode: code }),
  }));
}

/** US states that actually clear the offerable bar — sorted by name. */
export function offerableStates(): string[] {
  return allStatesForPicker()
    .filter((s) => s.offerable)
    .map((s) => s.code);
}

/**
 * The difficulty tiers that actually CLEAR the offerable bar for a scope, in
 * canonical order. `[]` for Landmarks (no difficulty step). Delegates to
 * `availableDifficulties` so the picker and the daily seed agree.
 */
export function difficultiesForScope(scope: Scope): Difficulty[] {
  return availableDifficulties(scope);
}

export interface TierEntry {
  difficulty: Difficulty;
  label: string;
  subtitle: string;
  offerable: boolean;
}

/**
 * ALL three tiers for a regional scope, each with its `offerable` flag — so the
 * "How challenging?" step can present the full Classic / Explorer / Adventurer
 * menu and simply DIM the tiers a thin region can't fill yet (rather than
 * hiding them — a senior expects to see the whole choice). Returns `[]` for
 * Landmarks. The default selection is the first offerable tier (Classic when it
 * clears the bar, per contract).
 */
export function allTiersForScope(scope: Scope): TierEntry[] {
  if (scope.kind === 'landmarks') return [];
  const offerable = new Set(availableDifficulties(scope));
  return DIFFICULTIES.map((d) => ({
    difficulty: d,
    label: difficultyLabel(d),
    subtitle: difficultySubtitle(d),
    offerable: offerable.has(d),
  }));
}

/** The default difficulty for a scope: Classic if offerable, else the first that is. */
export function defaultDifficultyForScope(scope: Scope): Difficulty {
  const offered = availableDifficulties(scope);
  if (offered.includes('classic')) return 'classic';
  return offered[0] ?? 'classic';
}

/** True if World is playable (it essentially always is). */
export function worldOfferable(): boolean {
  return scopeIsOfferable({ kind: 'world' });
}

export function landmarksOfferable(): boolean {
  return scopeIsOfferable({ kind: 'landmarks' });
}
